// Nepřítomnost (176): typy, barvy a počítání s daty — sdílené pro pracovníka,
// šichtovnici i schvalování na Přehledu.

export const ABSENCE_TYPES = [
  { value: "dovolena", code: "D", label: "Dovolená" },
  { value: "lekar", code: "L", label: "Lékař" },
  { value: "nemoc", code: "N", label: "Nemoc" },
  { value: "nahradni_volno", code: "NV", label: "Náhradní volno" },
  { value: "neplacene_volno", code: "NP", label: "Neplacené volno" },
];

// Barvy kódů — pozadí jemné, text sytý; ve tmavém režimu světlejší text.
const TONE = {
  D: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/50",
  N: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/50",
  L: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/50",
  NV: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/50",
  NP: "bg-stone-500/15 text-stone-700 dark:text-stone-300 border-stone-500/50",
  S: "bg-secondary text-muted-foreground border-border",
  P: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/50",
};
export const toneOf = (code) => TONE[code] || TONE.S;
// Pracovník hlásí předem (176e) — zpětně jen lékař, nemoc a neplacené volno.
export const ADVANCE_TYPES = ["dovolena", "nahradni_volno"];
export const typeOf = (value) => ABSENCE_TYPES.find((t) => t.value === value) || { value, code: "?", label: value };

export const STATUS_LABEL = { pending: "čeká", approved: "schváleno", rejected: "zamítnuto", cancelled: "staženo" };

// ---- datumy (bez časových pásem: pracujeme s YYYY-MM-DD) ----
export const pad2 = (n) => String(n).padStart(2, "0");
export const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const dateOf = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };
export const addDays = (iso, n) => { const d = dateOf(iso); d.setDate(d.getDate() + n); return isoOf(d); };
export const todayIso = () => isoOf(new Date());
export const monthStart = (iso) => `${iso.slice(0, 7)}-01`;
export const monthEnd = (iso) => { const d = dateOf(monthStart(iso)); d.setMonth(d.getMonth() + 1); d.setDate(0); return isoOf(d); };
export const addMonths = (iso, n) => { const d = dateOf(monthStart(iso)); d.setMonth(d.getMonth() + n); return isoOf(d); };
export const weekStart = (iso) => { const d = dateOf(iso); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return isoOf(d); };
export const DOW = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
export const dowOf = (iso) => DOW[(dateOf(iso).getDay() + 6) % 7];
export const MONTHS = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
export const monthLabel = (iso) => { const d = dateOf(iso); return `${MONTHS[d.getMonth()][0].toUpperCase()}${MONTHS[d.getMonth()].slice(1)} ${d.getFullYear()}`; };
export const czShort = (iso) => { const d = dateOf(iso); return `${d.getDate()}. ${d.getMonth() + 1}.`; };

// ISO týden (Po–Ne): číslo týdne a rok, ke kterému patří.
export function isoWeek(iso) {
  const d = dateOf(iso);
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const y0 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return { week: Math.ceil(((t - y0) / 86400000 + 1) / 7), year: t.getUTCFullYear() };
}

// Popisek týdne: „41. týden · 5.–11. 10. 2026"
export function weekLabel(iso) {
  const a = weekStart(iso), b = addDays(a, 6);
  const da = dateOf(a), db = dateOf(b);
  const left = da.getMonth() === db.getMonth() ? `${da.getDate()}.` : `${da.getDate()}. ${da.getMonth() + 1}.`;
  return `${isoWeek(iso).week}. týden · ${left}–${db.getDate()}. ${db.getMonth() + 1}. ${db.getFullYear()}`;
}

// Seskupení záznamů podle dne / týdne / měsíce (Docházka › Záznamy).
export function groupKey(iso, by) {
  if (by === "week") return weekStart(iso);
  if (by === "month") return iso.slice(0, 7);
  return iso;
}
export function groupLabel(key, by, formatDate) {
  if (by === "week") return weekLabel(key);
  if (by === "month") return monthLabel(`${key}-01`);
  return `${dowOf(key)} ${formatDate(key)}`;
}

// Státní svátky ČR (stejně jako backend) — Velký pátek a Velikonoční pondělí z data Velikonoc.
const holidayCache = {};
export function czHolidays(year) {
  if (holidayCache[year]) return holidayCache[year];
  const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const easter = isoOf(new Date(year, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1));
  const out = {};
  [["01-01", "Nový rok"], ["05-01", "Svátek práce"], ["05-08", "Den vítězství"], ["07-05", "Cyril a Metoděj"],
    ["07-06", "Mistr Jan Hus"], ["09-28", "Den české státnosti"], ["10-28", "Vznik Československa"],
    ["11-17", "Den boje za svobodu"], ["12-24", "Štědrý den"], ["12-25", "1. svátek vánoční"], ["12-26", "2. svátek vánoční"],
  ].forEach(([md, n]) => { out[`${year}-${md}`] = n; });
  out[addDays(easter, -2)] = "Velký pátek";
  out[addDays(easter, 1)] = "Velikonoční pondělí";
  holidayCache[year] = out;
  return out;
}
export const holidayOf = (iso) => czHolidays(Number(iso.slice(0, 4)))[iso] || null;
export const isWorkday = (iso) => { const w = dateOf(iso).getDay(); return w !== 0 && w !== 6 && !holidayOf(iso); };

// Kolik pracovních dní (Po–Pá bez svátků) je v rozsahu — stejně počítá backend.
export function weekdaysBetween(a, b) {
  if (!a || !b || b < a) return 0;
  let n = 0, cur = a;
  for (let i = 0; i < 400 && cur <= b; i += 1) {
    if (isWorkday(cur)) n += 1;
    cur = addDays(cur, 1);
  }
  return n;
}

export const daysWord = (n) => {
  const x = Number(n) || 0;
  const s = String(x).replace(".", ",");
  if (!Number.isInteger(x)) return `${s} dne`;
  return `${s} ${x === 1 ? "den" : x >= 2 && x <= 4 ? "dny" : "dní"}`;
};
